import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { UserPlus, MessageCircle, MessageSquare, Mail, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, cardUrl, withSource } from "@/lib/api";
import { useInviteText } from "@/components/InviteMessage";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const digits = (p) => (p || "").replace(/[^\d+]/g, "").replace(/^00/, "+");

export const InviteStatus = ({ c, testId }) => {
  if (c.joined_user_id) {
    return <Link to={`/friend/${c.joined_user_id}`} className="inline-flex items-center gap-1 mt-0.5 text-[11px] text-emerald-300 hover:text-emerald-200" data-testid={testId}><Check size={11} /> Joined</Link>;
  }
  if (c.invited_at) {
    const at = parseISO(c.invited_at);
    const when = Date.now() - at.getTime() < 60000 ? "just now" : formatDistanceToNowStrict(at, { addSuffix: true });
    return <span className="inline-block mt-0.5 text-[11px] text-amber-300/90" data-testid={testId}>Invited {when}</span>;
  }
  return null;
};

// Sends a saved contact their own invite link; when they join you get a friend request and we mark them Joined
export const InviteContact = ({ c, wishId, index, testId, inline = false }) => {
  const qc = useQueryClient();
  const { data: info } = useQuery({ queryKey: ["share-info"], queryFn: api.shareInfo, staleTime: 300000 });
  const { data: codes = {} } = useQuery({ queryKey: ["channel-sources"], queryFn: api.channelSources, staleTime: 300000 });
  const [fetched, setFetched] = useState({ name: "", code: "" });
  const [sentNow, setSentNow] = useState(false);
  const invite = useInviteText();
  // Rows are keyed by position, so never reuse another contact's code after a delete/reorder
  const code = c.invite_code || (fetched.name === c.name ? fetched.code : "");
  const ref = { wish_id: wishId, index, name: c.name };
  const prepare = (open) => {
    if (open && !code && wishId) api.inviteContact(ref).then((r) => setFetched({ name: c.name, code: r.code }), () => null);
  };
  useEffect(() => { if (inline) prepare(true); }, [inline, wishId, index]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!info || c.joined_user_id) return null;
  const markSent = () => {
    setSentNow(true);
    if (!wishId) return;
    api.inviteContact({ ...ref, sent: true }).then(() => qc.invalidateQueries({ queryKey: ["state"] }), () => null);
  };
  const first = (c.name || "").split(" ")[0];
  const text = (ch, cc = code) => `${first ? `Hi ${first}! ` : ""}${invite(withSource(cardUrl(info.handle), cc || codes[ch]))} Join and share your city with me so we can meet up when we're in the same place.`;
  const wa = digits(c.whatsapp || c.phone).replace("+", "");
  const phone = digits(c.phone || c.whatsapp);
  const build = (id, cc) => ({
    whatsapp: `https://wa.me/${wa}?text=${encodeURIComponent(text("whatsapp", cc))}`,
    sms: `sms:${phone}?&body=${encodeURIComponent(text("sms", cc))}`,
    email: `mailto:${c.email}?subject=${encodeURIComponent("Join me on the bucketlist")}&body=${encodeURIComponent(text("email", cc))}`,
  })[id];
  const options = [
    wa && { label: "WhatsApp", icon: MessageCircle, href: build("whatsapp"), id: "whatsapp" },
    phone && { label: "SMS", icon: MessageSquare, href: build("sms"), id: "sms" },
    c.email && { label: "Email", icon: Mail, href: build("email"), id: "email" },
  ].filter(Boolean);
  // A tap before this contact's code arrived: fetch it first so their join is still tracked
  const send = (o) => (e) => {
    if (code || !wishId) return markSent();
    e.preventDefault();
    setSentNow(true);
    api.inviteContact({ ...ref, sent: true }).then((r) => {
      setFetched({ name: c.name, code: r.code });
      qc.invalidateQueries({ queryKey: ["state"] });
      window.location.href = build(o.id, r.code);
    }, () => { window.location.href = o.href; });
  };
  const share = async () => {
    const t = text(navigator.share ? "native" : "copy");
    markSent();
    if (navigator.share) return navigator.share({ text: t }).catch(() => null);
    await navigator.clipboard.writeText(t).then(() => toast.success(`Invite copied — send it to ${first || "them"}`), () => toast.error("Couldn't copy"));
  };
  if (inline) {
    return (
      <div className="flex items-center gap-1.5" data-testid={testId}>
        {sentNow && <span className="flex items-center gap-1 text-[11px] text-emerald-300" data-testid={`${testId}-invited`}><Check size={11} /> Invited</span>}
        {options.map((o) => (
          <a key={o.id} href={o.href} onClick={send(o)} {...(o.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
            className="flex items-center gap-1 rounded-full bg-emerald-400/15 px-2.5 py-1.5 text-xs text-emerald-200 hover:bg-emerald-400/25 transition-colors" data-testid={`${testId}-${o.id}`}>
            <o.icon size={12} /> <span className="hidden min-[400px]:inline">{o.id === "sms" ? "Text" : o.label}</span>
          </a>
        ))}
      </div>
    );
  }
  return (
    <DropdownMenu onOpenChange={prepare}>
      <DropdownMenuTrigger asChild>
        <button type="button" className="p-1.5 text-emerald-300/80 hover:text-emerald-200" aria-label={`Invite ${c.name} to the app`} data-testid={testId}><UserPlus size={14} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid={`${testId}-menu`}>
        <DropdownMenuLabel className="text-xs font-normal text-slate-400 max-w-[220px]">
          {c.invited_at ? `Send ${first || "them"} a reminder` : `Invite ${first || "them"} to the app`} — when they join you'll be friends and they can share their city with you.
        </DropdownMenuLabel>
        {options.map((o) => (
          <DropdownMenuItem key={o.id} asChild>
            <a href={o.href} onClick={send(o)} {...(o.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})} className="flex items-center gap-2" data-testid={`${testId}-${o.id}`}><o.icon size={14} /> {o.label}</a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onSelect={share} className="flex items-center gap-2" data-testid={`${testId}-share`}><Share2 size={14} /> {navigator.share ? "Other app…" : "Copy invite"}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
